import { readonly } from 'vue';
import { Logger } from '~~/helpers/logger';
import type { PointsHistoryEntry } from '~~/types/pointsHistory/pointsHistory';

/**
 * The customer's points history from the Customer Data Platform, newest first. Kept apart from
 * useLoyaltyPoints: the history is only shown on the Partners dashboard and is a separate request
 * that can fail on its own, while the points status is read across the shop.
 */
export const useLoyaltyPointsHistory = () => {

  const pointsHistory = useState<PointsHistoryEntry[] | null>('loyaltyPointsHistory', () => null);
  const loading = useState<boolean>('loyaltyPointsHistoryLoading', () => false);
  const error = useState<boolean>('loyaltyPointsHistoryError', () => false);
  const requestId = useState<number>('loyaltyPointsHistoryRequestId', () => 0);
  const { shouldUseMockData, mockedPartnersData } = useExpondoPartnersMock();

  const isStaleRequest = (id: number) => id !== requestId.value;

  const applyMockedPointsHistory = () => {

    const mockedPointsHistory = mockedPartnersData.value?.pointsHistory ?? null;
    pointsHistory.value = mockedPointsHistory;
    error.value = mockedPointsHistory === null;

  };

  const load = async () => {

    if (shouldUseMockData.value) {

      applyMockedPointsHistory();

      return;

    }

    if (loading.value || pointsHistory.value) {

      return;

    }

    const id = ++requestId.value;
    loading.value = true;
    error.value = false;

    try {

      const response = await $fetch<PointsHistoryEntry[]>('/customer-data-platform/loyalty-history');

      if (!isStaleRequest(id)) {

        pointsHistory.value = response;

      }

    } catch (e) {

      if (!isStaleRequest(id)) {

        error.value = true;

      }

      Logger.error(`loyalty-points-history|fetch: Failed to fetch points history - ${e}`);

    } finally {

      if (!isStaleRequest(id)) {

        loading.value = false;

      }

    }

  };

  const reset = () => {

    requestId.value++;
    pointsHistory.value = null;
    loading.value = false;
    error.value = false;

  };

  return {
    pointsHistory: readonly(pointsHistory),
    loading: readonly(loading),
    error: readonly(error),
    load,
    reset,
  };

};
